/* eslint-disable import/no-unused-modules */
export const WIZARD_MODES = {
    CREATE: 'create',
    EDIT: 'edit'
};

export const WIZARD_STEPS = {
    STEP1: 'STEP1',
    STEP2: 'STEP2',
    STEP3: 'STEP3',
    STEP4: 'STEP4'
};

export const getWizardConfiguration = (mode = WIZARD_MODES.CREATE, translate = (key) => key) => ({
    mode,
    steps: [
        {
            id: WIZARD_STEPS.STEP1,
            key: 'basicInfo',
            title: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_BasicInfoTitle'),
            description: mode === WIZARD_MODES.CREATE
                ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_BasicInfoDescription')
                : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_BasicInfoUpdateDescription'),
            component: 'BasicInfoStep',
            icon: 'info-circle',
            isRequired: true,
            allowSkip: false,
            validationSchema: 'basicInfoValidation',
            props: {
                showSiteSelection: true,
                showNameField: true,
                showDescription: true
            }
        },
        {
            id: WIZARD_STEPS.STEP2,
            key: 'assetSelection',
            title: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_AssetSelectionTitle'),
            description: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_AssetSelectionDescription'),
            component: 'AssetSelectionStep',
            icon: 'layers',
            isRequired: true,
            allowSkip: false,
            validationSchema: 'assetSelectionValidation',
            props: {
                enableFiltering: true,
                enableAssetTypes: true,
                multiSelect: true,
                showAvailableAssets: true,
                showSelectedAssets: true
            }
        },
        {
            id: WIZARD_STEPS.STEP3,
            key: 'physicalConnection',
            title: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_PhysicalConnectionTitle'),
            description: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_PhysicalConnectionDescription'),
            component: 'PhysicalConnectionStep',
            icon: 'layers',
            isRequired: true,
            allowSkip: false,
            validationSchema: 'physicalConnectionValidation',
            props: {
                enableDragDrop: true,
                enableConnectionValidation: true,
                showConnectionSettings: true,
                allowParallelConnections: false,
                enforceSequence: true
            }
        },
        {
            id: WIZARD_STEPS.STEP4,
            key: 'review',
            title: mode === WIZARD_MODES.CREATE ?
                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_ReviewCreateTitle') :
                translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_ReviewUpdateTitle'),
            description: mode === WIZARD_MODES.CREATE
                ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_ReviewCreateDescription')
                : translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Steps_ReviewUpdateDescription'),
            component: 'ReviewStep',
            icon: 'check-circle',
            isRequired: true,
            allowSkip: false,
            validationSchema: 'reviewValidation',
            props: {
                showSummary: true,
                enableFinalValidation: true,
                showConfirmation: true
            }
        }
    ],
    navigation: {
        allowStepJumping: true,
        validateBeforeJump: true,
        saveProgressOnStep: true
    },
    ui: {
        showProgressBar: true,
        showStepNumbers: true,
        orientation: 'horizontal',
        theme: 'default'
    }
});

/**
 * Get step configuration by ID
 */
export const getStepById = (stepId, mode = WIZARD_MODES.CREATE, translate = (key) => key) => {
    const config = getWizardConfiguration(mode, translate);
    return config.steps.find(step => step.id === stepId);
};

/**
 * Get step configuration by key
 */
export const getStepByKey = (stepKey, mode = WIZARD_MODES.CREATE, translate = (key) => key) => {
    const config = getWizardConfiguration(mode, translate);
    return config.steps.find(step => step.key === stepKey);
};

/**
 * Navigation helper functions
 */
export const getNextStepId = (currentStepId, mode = WIZARD_MODES.CREATE, translate = (key) => key) => {
    const config = getWizardConfiguration(mode, translate);
    const currentIndex = config.steps.findIndex(step => step.id === currentStepId);

    if (currentIndex >= 0 && currentIndex < config.steps.length - 1) {
        return config.steps[currentIndex + 1].id;
    }
    return null;
};

export const getPreviousStepId = (currentStepId, mode = WIZARD_MODES.CREATE, translate = (key) => key) => {
    const config = getWizardConfiguration(mode, translate);
    const currentIndex = config.steps.findIndex(step => step.id === currentStepId);

    if (currentIndex > 0) {
        return config.steps[currentIndex - 1].id;
    }
    return null;
};

export const isFirstStep = (stepId, mode = WIZARD_MODES.CREATE, translate = (key) => key) => {
    const config = getWizardConfiguration(mode, translate);
    return config.steps.length > 0 && config.steps[0].id === stepId;
};

export const isLastStep = (stepId, mode = WIZARD_MODES.CREATE, translate = (key) => key) => {
    const config = getWizardConfiguration(mode, translate);
    return config.steps.length > 0 && config.steps[config.steps.length - 1].id === stepId;
};

/**
 * Initial form data structure
 */
export const getInitialFormData = () => ({
    basicInfo: {
        powertrainName: '',
        description: '',
        selectedSite: null,
        selectedLocation: null,
        selectedOrganization: null
    },
    assetSelection: {
        selectedAssets: [], // Each asset will have: { assetId, assetName, assetType, selectedSubType }
        addedAssets: [],
        availableAssets: [],
        selectedAssetTypeAvailableSection: null,
        assetFilterAvailableSection: '',
        selectedAssetTypeAddedSection: null,
        assetFilterAddedSection: ''
    },
    physicalConnection: {
        availableAssets: [],
        configuredConnections: [],
        connectionSequence: [],
        connectionSettings: {
            allowParallelConnections: false,
            validateConnections: true,
            enforceSequence: true
        }
    },
    review: {
        agreesToTerms: false,
        finalReviewCompleted: false
    },
    metadata: {
        createdBy: null,
        createdDate: null,
        lastModified: null,
        version: '1.0'
    }
});

/**
 * Transform Redux model data to form data structure
 * Enhanced to handle both create and edit modes properly
 */
export const transformModelToFormData = (model) => {
    // Handle assets - prefer selectedAssets, fallback to addedAssets
    const assets = model.selectedAssets || model.addedAssets || [];

    // Separate connected and not connected assets for edit mode
    const connectedAssets = model?.selectedPowertrain?.connectedAssets ||
        assets.filter(asset => asset.isConnected === true || asset.speedRatio > 0);
    const notConnectedAssets = model?.selectedPowertrain?.notConnectedAssets ||
        assets.filter(asset => asset.isConnected === false || (!asset.speedRatio || asset.speedRatio === 0));

    return {
        basicInfo: {
            powertrainName: model.powertrainName || '',
            description: model.description || '',
            selectedSite: model.selectedSite || null,
            selectedLocation: model.selectedLocation || null,
            selectedOrganization: model.selectedOrganization || null,
            // Include metadata for edit mode
            powertrainId: model.powertrainID || model.powertrainId || null,
            rowVersion: model.rowVersion || null
        },
        assetSelection: {
            selectedAssets: assets,
            addedAssets: assets,
            availableAssets: model.availableAssets || [],
            selectedAssetTypeAvailableSection: model.selectedAssetTypeAvailableSection || null,
            assetFilterAvailableSection: model.assetFilterAvailableSection || '',
            selectedAssetTypeAddedSection: model.selectedAssetTypeAddedSection || null,
            assetFilterAddedSection: model.assetFilterAddedSection || ''
        },
        physicalConnection: {
            availableAssets: assets,
            selectedAssets: assets,
            configuredConnections: connectedAssets,
            connectionSequence: connectedAssets.map(asset => asset.assetId?.toString() || asset.id),
            isSkipped: model.physicalConnection?.isSkipped ||
                connectedAssets.length === 0 && notConnectedAssets.length > 0,
            outputRatios: connectedAssets.reduce((acc, asset) => {
                if (asset.speedRatio) {
                    acc[asset.assetId || asset.id] = asset.speedRatio;
                }
                return acc;
            }, {}),
            physicalConnection: connectedAssets,
            connections: connectedAssets,
            connectionSettings: {
                allowParallelConnections: model.physicalConnection?.connectionSettings?.allowParallelConnections || false,
                validateConnections: model.physicalConnection?.connectionSettings?.validateConnections || true,
                enforceSequence: model.physicalConnection?.connectionSettings?.enforceSequence || true
            }
        },
        review: {
            agreesToTerms: false,
            finalReviewCompleted: false
        },
        metadata: {
            createdBy: model.createdBy || null,
            createdDate: model.createdDate || null,
            lastModified: model.lastModified || null,
            version: model.version || '1.0',
            powertrainId: model.powertrainID || model.powertrainId || null,
            rowVersion: model.rowVersion || null,
            isEditMode: model.isEditMode || false
        }
    };
};

/**
 * Transform form data back to Redux model structure
 */
export const transformFormDataToModel = (formData) => {
    return {
        powertrainName: formData.basicInfo?.powertrainName || '',
        description: formData.basicInfo?.description || '',
        selectedSite: formData.basicInfo?.selectedSite || null,
        selectedLocation: formData.basicInfo?.selectedLocation || null,
        selectedOrganization: formData.basicInfo?.selectedOrganization || null,
        selectedAssets: formData.assetSelection?.selectedAssets || formData.assetSelection?.addedAssets || [],
        addedAssets: formData.assetSelection?.addedAssets || [],
        availableAssets: formData.assetSelection?.availableAssets || [],
        selectedAssetTypeAvailableSection: formData.assetSelection?.selectedAssetTypeAvailableSection || null,
        assetFilterAvailableSection: formData.assetSelection?.assetFilterAvailableSection || '',
        selectedAssetTypeAddedSection: formData.assetSelection?.selectedAssetTypeAddedSection || null,
        assetFilterAddedSection: formData.assetSelection?.assetFilterAddedSection || '',
        physicalConnection: {
            availableAssets: formData.physicalConnection?.availableAssets || [],
            configuredConnections: formData.physicalConnection?.configuredConnections || [],
            connectionSequence: formData.physicalConnection?.connectionSequence || [],
            connectionSettings: formData.physicalConnection?.connectionSettings || {
                allowParallelConnections: false,
                validateConnections: true,
                enforceSequence: true
            }
        },
        createdBy: formData.metadata?.createdBy || null,
        createdDate: formData.metadata?.createdDate || null,
        lastModified: formData.metadata?.lastModified || null,
        version: formData.metadata?.version || '1.0'
    };
};
